import React, { useState, useCallback, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import UploadDocuments from '../UploadDocuments/UploadDocuments';
import Verification from '../Verification/Verification';
import Processing from '../Processing/Processing';
import Stepper from '../Stepper/Stepper';
import { ApplicationFlowContainer, ApplicationFlowWrapper } from './ApplicationFlow.styles';
import Assessment from '../Assessment/Assessment';
import Reports from '../Reports/Reports';

/*
  Steps are lazy-mounted on first visit, then kept mounted (hidden via the
  `hidden` attribute) for the rest of the session — that's what prevents
  data loss WITHIN a session. A hard refresh is a new session: the whole
  component tree remounts, location.state is gone, and every step's
  internal state (whatever it had in memory) is gone too — that part is
  unavoidable and expected.

  What refresh must still survive: applicationId, which step we're on,
  and a lock on going back to earlier steps — for BOTH flows:

  - Resumed flow (redirected here from ApplicationDetails because some
    stage was incomplete): lock floor = the original pending step, same
    as before.
  - Normal flow (fresh application, no redirect): no lock while the
    session is still alive in memory (going back is safe — the earlier
    step's component never unmounted, its state is intact). But once a
    refresh happens, everything before wherever you reloaded on truly
    lost its in-memory state, so from that point on the lock floor
    becomes "wherever you were when you reloaded."

  Mechanically: we snapshot location.state + the URL's search params
  exactly ONCE, on mount (initialNav below). That snapshot tells us
  whether this mount is a fresh redirect (state present), a reload
  (state absent but URL already had appId/step), or a brand-new flow
  (neither). minAllowedIndex is derived from that snapshot and never
  recalculated afterwards — so it doesn't ratchet forward just because
  the user progresses normally, only because they reload again (which
  remounts everything and re-snapshots at the new step).
*/

const STEP_ORDER = ['upload', 'verify', 'results', 'assessment', 'reports'];

// Which statusInfo field(s) gate each step being considered "done"
const STEP_STATUS_KEYS = {
  verify: ['verificationStatus'],
  results: ['extractionStatus', 'analysisStatus'],
  assessment: ['assessmentStatus'],
  reports: ['reportStatus'],
};

const normalizeStatusInfo = (raw) => {
  if (!raw) return null;
  return raw.statusInfo?.statusInfo || raw.statusInfo || raw;
};

// First step whose required fields aren't all COMPLETE
const resolveResumeStep = (statusInfo) => {
  const normalized = normalizeStatusInfo(statusInfo);
  if (!normalized) return null;

  for (const stepId of STEP_ORDER) {
    const keys = STEP_STATUS_KEYS[stepId];
    if (!keys) continue; // 'upload' has no status field — always considered done here
    const isComplete = keys.every((key) => normalized[key] === 'COMPLETE');
    if (!isComplete) return stepId;
  }
  return null; // everything complete — shouldn't happen, ApplicationDetails handles that case
};

const ApplicationFlow = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Snapshot once, on mount — before our own sync effect (below) ever
  // touches the URL. This is the one-time source of truth for how this
  // particular mount started.
  const [initialNav] = useState(() => {
    const stateApplicationId = location.state?.applicationId ?? null;
    const urlApplicationId = searchParams.get('appId');
    const urlStep = searchParams.get('step');
    const urlResume = searchParams.get('resume');

    const applicationId = stateApplicationId ?? urlApplicationId ?? null;

    const resumeStep = stateApplicationId
      ? resolveResumeStep(location.state.statusInfo)
      : urlApplicationId && STEP_ORDER.includes(urlResume)
        ? urlResume
        : null;

    const step =
      (applicationId && STEP_ORDER.includes(urlStep) && urlStep) || resumeStep || 'upload';

    // A reload happened iff we landed on an in-progress step/applicationId
    // purely from the URL, with no fresh location.state to explain it.
    const isReload = !stateApplicationId && !!urlApplicationId;

    const resumeFloor = resumeStep ? STEP_ORDER.indexOf(resumeStep) : 0;
    const reloadFloor = isReload ? STEP_ORDER.indexOf(step) : 0;

    return {
      applicationId,
      resumeStep,
      step,
      minAllowedIndex: Math.max(resumeFloor, reloadFloor),
    };
  });

  const [step, setStep] = useState(initialNav.step);
  const [applicationId, setApplicationId] = useState(initialNav.applicationId);
  const [visited, setVisited] = useState({ [initialNav.step]: true });

  // Keep the URL in sync so a refresh at any point can restore
  // applicationId + step (+ the resume floor, if this was a resumed
  // flow). No-op until applicationId exists, so a brand-new
  // "/uploadDocuments" with nothing yet never gains query params.
  useEffect(() => {
    if (!applicationId) return;
    const params = { appId: applicationId, step };
    if (initialNav.resumeStep) params.resume = initialNav.resumeStep;
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId, step]);

  const goTo = useCallback((nextStep) => {
    setStep(nextStep);
    setVisited((prev) => (prev[nextStep] ? prev : { ...prev, [nextStep]: true }));
  }, []);

  // Stepper only allows clicking back into steps already reached, and
  // never before minAllowedIndex. That floor is either the original
  // pending step (resumed flow) or wherever the user was when they last
  // reloaded (normal flow, post-refresh) — whichever is furthest along.
  const handleStepperNavigate = (targetStep) => {
    const targetIdx = STEP_ORDER.indexOf(targetStep);
    const currentIdx = STEP_ORDER.indexOf(step);
    if (targetIdx < initialNav.minAllowedIndex) return;
    if (targetIdx < currentIdx) goTo(targetStep);
  };

  const handleUploadSaved = (newApplicationId) => {
    setApplicationId(newApplicationId);
    goTo('verify');
  };

  const handleGenerateReport = () => goTo('results');
  const handleProcessingContinue = () => goTo('assessment');
  const handleAssessmentContinue = () => goTo('reports');
  const handleDashboard = () => navigate('/');

  return (
    <ApplicationFlowWrapper>
      <ApplicationFlowContainer>
        <Stepper
          current={step}
          onNavigate={handleStepperNavigate}
          minAllowed={initialNav.minAllowedIndex}
        />

        {visited.upload && (
          <div hidden={step !== 'upload'}>
            <UploadDocuments onSaved={handleUploadSaved} onDashboard={handleDashboard} />
          </div>
        )}

        {visited.verify && (
          <div hidden={step !== 'verify'}>
            <Verification
              applicationId={applicationId}
              onGenerateReport={handleGenerateReport}
              onDashboard={handleDashboard}
            />
          </div>
        )}

        {visited.results && (
          <div hidden={step !== 'results'}>
            <Processing
              applicationId={applicationId}
              onContinue={handleProcessingContinue}
              onDashboard={handleDashboard}
            />
          </div>
        )}

        {visited.assessment && (
          <div hidden={step !== 'assessment'}>
            <Assessment
              listId={applicationId}
              onContinue={handleAssessmentContinue}
              onDashboard={handleDashboard}
            />
          </div>
        )}

        {visited.reports && (
          <div hidden={step !== 'reports'}>
            <Reports listId={applicationId} onDashboard={handleDashboard} />
          </div>
        )}
      </ApplicationFlowContainer>
    </ApplicationFlowWrapper>
  );
};

export default ApplicationFlow;
